import { FormSection, Link, List, Text, Timeline } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { never } from '@common/utils/never';
import { useProcessStartWizardWithCards } from '@frontend/utils/hooks/useProcessStartWizardWithCards';

export enum HolterProcedureType {
    External = 'external',
    Home = 'home',
    Practice = 'practice',
}

interface ITcohFollowingStepsProps {
    holterType: HolterProcedureType;
}
export function TcohFollowingSteps({ holterType }: ITcohFollowingStepsProps) {
    const hasProcessStartWizardWithCards = useProcessStartWizardWithCards();
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    if (!hasProcessStartWizardWithCards) {
        return null;
    }
    switch (holterType) {
        case HolterProcedureType.Home:
            return (
                <FormSection title="Vervolgstappen Holter onderzoek (thuisservice)">
                    <Timeline>
                        <Timeline.Item>
                            <Timeline.ItemHeading>
                                Ksyos stelt de holter in een stuurt deze naar de patiënt
                            </Timeline.ItemHeading>
                            <List
                                size="small"
                                styleType="bullet"
                            >
                                <List.Item>
                                    De patiënt sluit thuis de holter aan en draagt deze 1, 2, 3 of 7 dagen
                                </List.Item>
                                <List.Item>
                                    De patiënt noteert de klachten in het dagboekje gedurende het onderzoek
                                </List.Item>
                                <List.Item>
                                    De patiënt stuurt na de afgesproken tijd de holter terug naar Ksyos
                                </List.Item>
                            </List>
                        </Timeline.Item>

                        <Timeline.Item>
                            Ksyos leest de holter uit en laadt de onderzoeksdata in het Ksyos platform, en een
                            analyserapport wordt gemaakt
                        </Timeline.Item>

                        <Timeline.Item>
                            <Timeline.ItemHeading>Een Ksyos cardioloog beoordeelt het onderzoek</Timeline.ItemHeading>
                            <Text
                                as="span"
                                size="sm"
                            >
                                Bij een ernstige afwijking treedt er een spoedprotocol in werking. De cardioloog
                                beoordeelt direct het onderzoek. Je wordt telefonisch geïnformeerd door Ksyos binnen 1
                                werkdag na uitlezen van de holter.
                            </Text>
                        </Timeline.Item>

                        <Timeline.Item>
                            Binnen 3 werkdagen na uitlezen holter ontvang je de uitslag in het Ksyos platform en via
                            Edifact in je HIS als je je{' '}
                            <Link
                                openInNewTab
                                to={`/employments/${selectedEmploymentId}/personalSettings`}
                                variant="link"
                            >
                                zorgmailnummer hebt doorgegeven
                            </Link>
                        </Timeline.Item>

                        <Timeline.Item>
                            Optioneel: Je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het
                            vervolgbeleid (via een consult)
                        </Timeline.Item>
                    </Timeline>
                </FormSection>
            );
        case HolterProcedureType.External:
            return (
                <FormSection title="Vervolgstappen Holter onderzoek (extern)">
                    <Timeline>
                        <Timeline.Item>
                            Het MDC maakt een afspraak met de patiënt voor het aansluiten van de holter
                        </Timeline.Item>

                        <Timeline.Item>
                            <Timeline.ItemHeading>
                                Het MDC stelt de holter in via het Ksyos platform en sluit de holter aan bij de patiënt
                            </Timeline.ItemHeading>
                            <List
                                size="small"
                                styleType="bullet"
                            >
                                <List.Item>De patiënt draagt de holter 1, 2, 3 of 7 dagen</List.Item>
                                <List.Item>
                                    De patiënt noteert de klachten in het dagboekje gedurende het onderzoek
                                </List.Item>
                                <List.Item>De patiënt levert na de afgesproken tijd de holter in bij het MDC</List.Item>
                            </List>
                        </Timeline.Item>

                        <Timeline.Item>
                            Het MDC leest de holter uit via Ksyos platform en laadt de onderzoeksdata, en een
                            analyserapport wordt gemaakt
                        </Timeline.Item>

                        <Timeline.Item>
                            <Timeline.ItemHeading>Een Ksyos cardioloog beoordeelt het onderzoek</Timeline.ItemHeading>
                            <Text
                                as="span"
                                size="sm"
                            >
                                Bij een ernstige afwijking treedt er een spoedprotocol in werking. De cardioloog
                                beoordeelt direct het onderzoek. Je wordt telefonisch geïnformeerd door Ksyos binnen 1
                                werkdag na uitlezen van de holter.
                            </Text>
                        </Timeline.Item>

                        <Timeline.Item>
                            Binnen 3 werkdagen na uitlezen holter ontvang je de uitslag in het Ksyos platform en via
                            Edifact in je HIS als je je{' '}
                            <Link
                                to={`/employments/${selectedEmploymentId}/personalSettings`}
                                variant="link"
                            >
                                zorgmailnummer hebt doorgegeven
                            </Link>
                        </Timeline.Item>

                        <Timeline.Item>
                            Optioneel: Je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het
                            vervolgbeleid (via een consult)
                        </Timeline.Item>
                    </Timeline>
                </FormSection>
            );
        case HolterProcedureType.Practice:
            return (
                <FormSection title="Vervolgstappen Holter onderzoek (op de praktijk)">
                    <Timeline>
                        <Timeline.Item>
                            Je maakt een afspraak met de patiënt voor het aansluiten van de holter of doet dit direct na
                            het consult
                        </Timeline.Item>

                        <Timeline.Item>
                            <Timeline.ItemHeading>
                                Je stelt de holter in via het Ksyos platform en sluit de deze aan bij de patient
                            </Timeline.ItemHeading>
                            <List
                                size="small"
                                styleType="bullet"
                            >
                                <List.Item>De patiënt draagt de holter 1, 2, 3 of 7 dagen</List.Item>
                                <List.Item>
                                    De patiënt noteert de klachten in het dagboekje gedurende het onderzoek
                                </List.Item>
                                <List.Item>
                                    De patiënt levert de holter na de afgesproken tijd in op de praktijk
                                </List.Item>
                            </List>
                        </Timeline.Item>

                        <Timeline.Item>
                            Een assistent of POH uit jouw praktijk leest de holter uit via het Ksyos platform en laadt
                            de onderzoeksdata, en een analyserapport wordt gemaakt
                        </Timeline.Item>

                        <Timeline.Item>
                            <Timeline.ItemHeading>De cardioloog beoordeelt het onderzoek</Timeline.ItemHeading>
                            <Text
                                as="span"
                                size="sm"
                            >
                                Bij een ernstige afwijking treedt er een spoedprotocol in werking. De cardioloog
                                beoordeelt direct het onderzoek. Je wordt telefonisch geïnformeerd door Ksyos binnen 1
                                werkdag na uitlezen van de holter.
                            </Text>
                        </Timeline.Item>

                        <Timeline.Item>
                            Binnen 3 werkdagen na uitlezen holter ontvang je de uitslag in het Ksyos platform en via
                            Edifact in je HIS als je je{' '}
                            <Link
                                to={`/employments/${selectedEmploymentId}/personalSettings`}
                                variant="link"
                            >
                                zorgmailnummer hebt doorgegeven
                            </Link>
                        </Timeline.Item>

                        <Timeline.Item>
                            Optioneel: Je stuurt het onderzoek door naar een Ksyos cardioloog voor advies over het
                            vervolgbeleid (via een consult)
                        </Timeline.Item>
                    </Timeline>
                </FormSection>
            );
        default:
            never(holterType);
    }
}
